// gen.tsx — turn an endpoint spec into idiomatic code samples per language.
// An endpoint spec: { method, path, resource, action, args:[...], body:{...} }
// action ∈ create | list | retrieve | update

const API_BASE = 'https://api.melio.com';

export interface Endpoint {
  method: string;
  path: string;
  resource: string;
  action: string;
  args?: string[];
  body?: Record<string, unknown>;
  resultVar?: string;
}

function snake(s: string): string {
  return s.replace(/([A-Z])/g, '_$1').toLowerCase();
}
function pascal(s: string): string {
  return (
    s.charAt(0).toUpperCase() +
    s
      .slice(1)
      .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      .replace(/Id$/, 'ID')
  );
}

// ---- JSON (for cURL -d and response panes) ------------------------------
function jsonStr(obj: unknown, indent = 0): string {
  const pad = '  '.repeat(indent),
    pad1 = '  '.repeat(indent + 1);
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    return '[\n' + obj.map((v) => pad1 + jsonStr(v, indent + 1)).join(',\n') + '\n' + pad + ']';
  }
  if (obj && typeof obj === 'object') {
    const keys = Object.keys(obj);
    if (keys.length === 0) return '{}';
    return (
      '{\n' + keys.map((k) => `${pad1}"${k}": ${jsonStr((obj as Record<string, unknown>)[k], indent + 1)}`).join(',\n') + '\n' + pad + '}'
    );
  }
  if (typeof obj === 'string') return `"${obj}"`;
  return String(obj);
}

// ---- cURL ---------------------------------------------------------------
function genCurl(ep: Endpoint, key: string, baseUrl = API_BASE): string {
  // ep.path starts with /v2/... and baseUrl may or may not include /v2 — normalise here.
  const url = baseUrl.endsWith('/v2') ? `${baseUrl}${ep.path.replace(/^\/v2/, '')}` : `${baseUrl}${ep.path}`;
  const lines = [`curl ${url} \\`];
  lines.push(`  -H "api-key: ${key}" \\`);
  if (ep.body) lines.push(`  -H "Content-Type: application/json" \\`);
  if (ep.method !== 'GET') lines.push(`  -X ${ep.method}${ep.body ? ' \\' : ''}`);
  if (ep.body) {
    const j = jsonStr(ep.body, 0)
      .split('\n')
      .map((l, i) => (i === 0 ? l : '  ' + l))
      .join('\n');
    lines.push(`  -d '${j}'`);
  }
  return lines.join('\n').replace(/\\\n\s*$/, '');
}

// ---- value formatters per language --------------------------------------
function nodeVal(v: unknown, ind: number): string {
  const pad = '  '.repeat(ind),
    pad1 = '  '.repeat(ind + 1);
  if (Array.isArray(v)) return '[\n' + v.map((x) => pad1 + nodeVal(x, ind + 1)).join(',\n') + '\n' + pad + ']';
  if (v && typeof v === 'object')
    return (
      '{\n' +
      Object.keys(v)
        .map((k) => `${pad1}${/^[a-zA-Z_$][\w$]*$/.test(k) ? k : '"' + k + '"'}: ${nodeVal((v as Record<string, unknown>)[k], ind + 1)}`)
        .join(',\n') +
      '\n' +
      pad +
      '}'
    );
  if (typeof v === 'string') return `"${v}"`;
  return String(v);
}
function pyVal(v: unknown, ind: number): string {
  const pad = '    '.repeat(ind),
    pad1 = '    '.repeat(ind + 1);
  if (Array.isArray(v)) return '[\n' + v.map((x) => pad1 + pyVal(x, ind + 1)).join(',\n') + '\n' + pad + ']';
  if (v && typeof v === 'object')
    return (
      '{\n' +
      Object.keys(v)
        .map((k) => `${pad1}"${snake(k)}": ${pyVal((v as Record<string, unknown>)[k], ind + 1)}`)
        .join(',\n') +
      '\n' +
      pad +
      '}'
    );
  if (typeof v === 'string') return `"${v}"`;
  if (v === true) return 'True';
  if (v === false) return 'False';
  return String(v);
}
function goVal(v: unknown, ind: number, typeName?: string): string {
  const pad = '\t'.repeat(ind),
    pad1 = '\t'.repeat(ind + 1);
  if (Array.isArray(v)) return `[]melio.${typeName || 'Param'}{\n` + v.map((x) => pad1 + goVal(x, ind + 1)).join(',\n') + ',\n' + pad + '}';
  if (v && typeof v === 'object')
    return (
      `&melio.${typeName || 'Params'}{\n` +
      Object.keys(v)
        .map((k) => `${pad1}${pascal(k)}: ${goVal((v as Record<string, unknown>)[k], ind + 1)}`)
        .join(',\n') +
      ',\n' +
      pad +
      '}'
    );
  if (typeof v === 'string') return `melio.String("${v}")`;
  if (typeof v === 'number') return `melio.Int(${v})`;
  return String(v);
}

const RES_LABEL: Record<string, string> = {
  entities: 'entities',
  accounts: 'accounts',
  accountLinks: 'accountLinks',
  accountVerifications: 'accountVerifications',
  payments: 'payments',
  paymentActions: 'paymentActions',
  webhook: 'webhook',
  webhookEvents: 'webhookEvents',
  addressSuggestions: 'addressSuggestions',
  addressPlaces: 'addressPlaces',
  industryTypes: 'industryTypes',
};
// Go struct type names for request bodies — only resources with a request body need an entry.
const RES_PARAM: Record<string, string> = {
  entities: 'EntityParams',
  accounts: 'AccountParams',
  accountLinks: 'AccountLinkParams',
  payments: 'PaymentParams',
  paymentActions: 'PaymentActionParams',
  webhook: 'WebhookParams',
};

// ---- Node SDK -----------------------------------------------------------
function genNode(ep: Endpoint, key: string): string {
  const res = RES_LABEL[ep.resource] || ep.resource;
  const fn = { create: 'create', list: 'list', retrieve: 'retrieve', update: 'update', delete: 'delete' }[ep.action];
  const args = (ep.args || []).map((a) => `"${a}"`);
  if (ep.body) args.push(nodeVal(ep.body, 0));
  const assignTo = ep.action === 'list' ? 'result' : ep.resultVar || 'result';
  return [
    `import { Melio } from "@melio/sdk";`,
    ``,
    `const melio = new Melio("${key}");`,
    ``,
    `const ${assignTo} = await melio.${res}.${fn}(${args.length > 1 ? '\n  ' + args.join(',\n  ') + '\n' : args.join(', ')});`,
    ``,
    `console.log(${assignTo}.id ?? ${assignTo});`,
  ].join('\n');
}

// ---- Python SDK ---------------------------------------------------------
function genPython(ep: Endpoint, key: string): string {
  const res = snake(RES_LABEL[ep.resource] || ep.resource);
  const fn = ep.action;
  const posArgs = (ep.args || []).map((a) => `"${a}"`);
  let call: string;
  if (ep.body) {
    const kwargs = Object.keys(ep.body).map((k) => `    ${snake(k)}=${pyVal(ep.body![k], 1).replace(/^\s+/, '')}`);
    call = `melio.${res}.${fn}(\n${[...posArgs.map((a) => '    ' + a), ...kwargs].join(',\n')},\n)`;
  } else {
    call = `melio.${res}.${fn}(${posArgs.join(', ')})`;
  }
  return [
    `import os`,
    `from melio import Melio`,
    ``,
    `melio = Melio(os.environ["MELIO_API_KEY"])`,
    ``,
    `result = ${call}`,
    ``,
    `print(result.id)`,
  ].join('\n');
}

// ---- Go SDK -------------------------------------------------------------
function genGo(ep: Endpoint, key: string): string {
  const res = pascal(RES_LABEL[ep.resource] || ep.resource);
  const fn = { create: 'Create', list: 'List', retrieve: 'Retrieve', update: 'Update', delete: 'Delete' }[ep.action];
  const args = ['context.Background()', ...(ep.args || []).map((a) => `"${a}"`)];
  if (ep.body) args.push(goVal(ep.body, 2, RES_PARAM[ep.resource]).replace(/^\t+/, ''));
  const inner = args.length > 2 && ep.body ? args.slice(0, -1).join(', ') + ', ' + args[args.length - 1] : args.join(', ');
  return [
    `package main`,
    ``,
    `import (`,
    `\t"context"`,
    `\t"fmt"`,
    ``,
    `\tmelio "github.com/melio/melio-go"`,
    `)`,
    ``,
    `func main() {`,
    `\tclient := melio.New("${key}")`,
    ``,
    `\tresult, err := client.${res}.${fn}(${inner})`,
    `\tif err != nil {`,
    `\t\tpanic(err)`,
    `\t}`,
    `\tfmt.Println(result.ID)`,
    `}`,
  ].join('\n');
}

function genAll(ep: Endpoint, key: string, baseUrl = API_BASE): Record<string, string> {
  return { curl: genCurl(ep, key, baseUrl), node: genNode(ep, key), python: genPython(ep, key), go: genGo(ep, key) };
}

export { API_BASE, jsonStr, genCurl, genNode, genPython, genGo, genAll, snake, pascal };
